/**
 * Client-safe referral and cookie utilities.
 * Shared between Form.tsx and ReferrerHandler.tsx.
 */

/** Normalize a referral code: trim whitespace, remove internal spaces, uppercase. */
export function normalizeReferralCode(code: string): string {
  return code.trim().replace(/\s+/g, "").toUpperCase();
}

/**
 * Derive the root domain for cookie setting.
 * Returns `null` for localhost and raw IP addresses (no domain cookie).
 */
export function getCookieDomain(): string | null {
  const hostname = window.location.hostname;
  if (hostname === "localhost") return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;
  const parts = hostname.split(".");
  if (parts.length < 2) return null;
  return parts.slice(-2).join(".");
}

/** Set a cookie with optional root-domain scoping. */
export function setCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
): void {
  const domain = getCookieDomain();
  const domainPart = domain ? `; domain=.${domain}` : "";
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; path=/${domainPart}; max-age=${maxAgeSeconds}`;
}

/** Read the value of a cookie by name. Returns `null` if not found. */
export function getCookieValue(name: string): string | null {
  const parts = document.cookie.split("; ").filter(Boolean);
  const match = parts.find((p) => p.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(`${name}=`.length));
}
